import {batchActions} from 'redux-batch-enhancer'
import {CALL_API, Call} from 'redux/middleware/api'
import {getCurrentSitePlease} from 'redux/modules/currentUser/actions'
import {getCurrentSite} from 'redux/modules/currentUser/selectors'
import {reportError} from 'redux/modules/error/actions'
import formatDateWithTz from 'utils/formatDateWithTz'
import json2csv from 'json2csv'
import moment from 'moment'
import * as _ from 'lodash'
import get from 'lodash/get'

import {
  ORDERS,
  ORDERS_STATS,
  ORDERS_BY_ID,
  ORDERS_PER_PAGE,
  ORDERS_SET_SESSION_FILTER,
  ORDERS_SET_FILTER,
  ORDERS_RESET_FILTERS,
  ORDERS_RESET_ALL,
  EXPORT_ORDERS,
  ORDERS_SET_SESSIONS_ONLY
} from './'

function loadOrders({
  merchantSiteId,
  targetCurrency,
  skip = 0,
  startDate,
  endDate,
  filter,
  sessions,
  sessionsOnly = false
}) {
  const queryParams = {
    merchantSiteId
  }
  const body = {
    range: {
      start: startDate,
      end: endDate
    },
    skip,
    limit: ORDERS_PER_PAGE,
    filter,
    sessions,
    sessionsOnly,
    targetCurrency
  }

  return {
    [CALL_API]: {
      endpoint: 'Orders/withSessions',
      method: 'POST',
      types: ORDERS,
      queryParams,
      body
    }
  }
}

function fetchExportOrders({
  merchantSiteId,
  limit,
  targetCurrency,
  skip = 0,
  startDate,
  endDate,
  filter,
  sessions
}) {
  const queryParams = {
    merchantSiteId
  }
  const body = {
    range: {
      start: startDate,
      end: endDate
    },
    skip,
    limit: limit || ORDERS_PER_PAGE,
    filter,
    sessions,
    targetCurrency
  }

  return {
    [CALL_API]: {
      endpoint: 'Orders/withSessions',
      method: 'POST',
      types: EXPORT_ORDERS,
      queryParams,
      body
    }
  }
}

function loadOrdersStats({merchantSiteId, targetCurrency, startDate, endDate}) {
  const queryParams = {
    merchantSiteId
  }
  const body = {
    range: {
      start: startDate,
      end: endDate
    },
    skip: 0,
    limit: 0,
    onlyStats: true,
    targetCurrency
  }

  return {
    [CALL_API]: {
      endpoint: 'Orders/withSessions',
      method: 'POST',
      types: ORDERS_STATS,
      queryParams,
      body
    }
  }
}

function loadOrdersById({merchantSiteId, id}) {
  const queryParams = {
    merchantSiteId
  }
  const body = {
    filter: {id}
  }

  return {
    [CALL_API]: {
      endpoint: 'Orders/withSessions',
      types: ORDERS_BY_ID,
      method: 'POST',
      queryParams,
      body
    }
  }
}

export function setActiveFilter(type, value) {
  return {
    type: ORDERS_SET_FILTER,
    filterType: type,
    filterValue: value
  }
}

export function setSessionsOnly(value) {
  return {
    type: ORDERS_SET_SESSIONS_ONLY,
    value
  }
}

export function setSessionFilter(type, value, channelTypes) {
  return {
    type: ORDERS_SET_SESSION_FILTER,
    filterType: type,
    filterValue: value,
    channelTypes: channelTypes
  }
}

export function resetActiveFilters() {
  return {
    type: ORDERS_RESET_FILTERS
  }
}

export function resetFiltersAndStats() {
  return {
    type: ORDERS_RESET_ALL
  }
}

export function fetchOrdersWithStats(
  page = 0,
  startDate,
  endDate,
  currency,
  timezone,
  filter,
  sessions,
  sessionsOnly
) {
  return (dispatch, getState) => {
    const {id} = getCurrentSite(getState()) || {}
    const fetchParams = {
      merchantSiteId: id,
      skip: ORDERS_PER_PAGE * page,
      startDate: formatDateWithTz(startDate, timezone),
      endDate: formatDateWithTz(endDate, timezone),
      filter,
      sessions,
      sessionsOnly,
      targetCurrency: currency
    }

    Call.all(
      dispatch(
        batchActions([loadOrders(fetchParams), loadOrdersStats(fetchParams)])
      )
    ).fail(dispatch(reportError()))
  }
}

export function fetchOrdersById(ids) {
  return (dispatch, getState) =>
    new Call((success, fail) => {
      getCurrentSitePlease(dispatch, getState).success(merchantSiteId => {
        const fetchParams = {merchantSiteId, id: ids}
        dispatch(loadOrdersById(fetchParams))
          .success(success)
          .fail(fail)
          .fail(dispatch(reportError()))
      })
    })
}

export function exportOrder(
  startDate,
  endDate,
  currency,
  timezone,
  filter,
  sessions
) {
  return (dispatch, getState) => {
    const {id} = getCurrentSite(getState()) || {}
    const fetchParams = {
      merchantSiteId: id,
      skip: 0,
      limit: 1000000,
      startDate: formatDateWithTz(startDate, timezone),
      endDate: formatDateWithTz(endDate, timezone),
      filter,
      sessions,
      targetCurrency: currency
    }

    return dispatch(fetchExportOrders(fetchParams))
      .success(result => {
        const orders = result.orders
        const data = orders.map(order => {
          const {sessions, conversions} = order
          const conversionTouchs = (conversions || []).map(conversionReducer)
          const touchpoints = [...sessions, ...conversionTouchs].sort(
            (t1, t2) => {
              return t1.createdAt - t2.createdAt
            }
          )

          const touchpointStrs = []
          touchpoints.map((touchpoint, index) => {
            if (touchpoint.type === 'conversion') {
              touchpointStrs.push(touchpoint.conversionType)
            } else {
              if (_.keys(touchpoint.impressions).length) {
                const impressions = _.map(touchpoint.impressions, impression =>
                  JSON.parse(impression)
                )
                let impresionsGroups = _.groupBy(impressions, impression => {
                  return (
                    impression.channelGroup.toLowerCase() +
                    impression.channel.toLowerCase()
                  )
                })
  
                impresionsGroups = _.sortBy(impresionsGroups, [
                  group => {
                    const minlastView = _.minBy(group, 'createdAt')
                    return minlastView['createdAt']
                  }
                ])
  
                _.map(impresionsGroups, group => {
                  touchpointStrs.push(group[0].channel)
                })
              }
              touchpointStrs.push(touchpoint.channel.dv_channel)
            }
          })

          const createdAt = moment
            .tz(order.createdAt, timezone)
            .format('YYYY-MM-DD HH:mm Z')
          return {
            orderId: order.orderId,
            createdAt: createdAt,
            touchpoints: touchpointStrs.join(' - ')
          }
        })

        const csvHeader = 'data:text/csv;charset=utf-8,'
        const csvFile = encodeURI(
          csvHeader +
            json2csv({
              data,
              fields: ['orderId', 'createdAt', 'touchpoints'],
              fieldNames: ['Customer ID', 'Last Conversion Time', 'Touchpoints']
            })
        )
        const link = document.createElement('a')
        link.setAttribute('href', csvFile)
        const timestamp = new Date().getTime()
        link.setAttribute('download', `conversion export.${timestamp}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      })
      .fail(dispatch(reportError()))
  }
}

function conversionReducer(conversion) {
  const startedAt = get(conversion, 'time', null)
  const conversionType = get(conversion, 'type', null)

  return {
    type: 'conversion',
    startedAt,
    conversionType: conversionType !== 'other' ? conversionType : 'Reg finished/FTD'
  }
}



// WEBPACK FOOTER //
// ./client/redux/modules/orders/actions.js